import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FolderOpen,
  Search,
  Image as ImageIcon,
  Film,
  FileText,
  Figma,
  HardDrive,
  Link as LinkIcon,
  Download,
  Copy,
  ExternalLink,
  ArrowUpRight,
  LayoutGrid,
  List as ListIcon,
  X,
  RotateCcw,
  Sparkles,
  Tag,
  Pencil,
  Wand2,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  image: { Icon: ImageIcon, color: 'bg-violet-100 text-violet-700 border-violet-200', dot: 'from-violet-500 to-fuchsia-500', label: 'IMAGE' },
  video: { Icon: Film, color: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'from-rose-500 to-pink-500', label: 'VIDEO' },
  doc:   { Icon: FileText, color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'from-amber-500 to-orange-500', label: 'DOC' },
  figma: { Icon: Figma, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'from-emerald-500 to-teal-500', label: 'FIGMA' },
  drive: { Icon: HardDrive, color: 'bg-sky-100 text-sky-700 border-sky-200', dot: 'from-sky-500 to-blue-500', label: 'DRIVE' },
  link:  { Icon: LinkIcon, color: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'from-slate-400 to-slate-500', label: 'LINK' },
  other: { Icon: LinkIcon, color: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'from-slate-400 to-slate-500', label: 'FILE' },
};

// --- Thumbnail resolution -----------------------------------------------------
// Resolve a poster/thumbnail URL for well-known providers so the grid feels real.
// If we can't resolve one, returns null and the card falls back to the icon poster.

const youtubeIdFrom = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.split('/').filter(Boolean)[0] || null;
    if (u.hostname.includes('youtube.com')) {
      if (u.searchParams.get('v')) return u.searchParams.get('v');
      const parts = u.pathname.split('/').filter(Boolean);
      // /embed/{id} or /shorts/{id}
      if (parts[0] === 'embed' || parts[0] === 'shorts') return parts[1] || null;
    }
    return null;
  } catch { return null; }
};

const driveIdFrom = (url) => {
  try {
    const u = new URL(url);
    if (!u.hostname.includes('drive.google.com')) return null;
    // /file/d/{ID}/view
    const m = u.pathname.match(/\/d\/([a-zA-Z0-9_-]{15,})/);
    if (m) return m[1];
    // ?id={ID}
    if (u.searchParams.get('id')) return u.searchParams.get('id');
    return null;
  } catch { return null; }
};

const loomIdFrom = (url) => {
  try {
    const u = new URL(url);
    if (!u.hostname.includes('loom.com')) return null;
    const parts = u.pathname.split('/').filter(Boolean);
    if (parts[0] === 'share' || parts[0] === 'embed') return parts[1] || null;
    return null;
  } catch { return null; }
};

const previewUrlFor = (asset) => {
  const url = asset.url || '';
  // 1. Direct image — use as-is
  if (asset.kind === 'image') return url;
  // 2. YouTube — use the well-known thumbnail endpoint
  const yt = youtubeIdFrom(url);
  if (yt) return `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;
  // 3. Public Google Drive — use Google's thumbnail proxy
  const drv = driveIdFrom(url);
  if (drv) return `https://drive.google.com/thumbnail?id=${drv}&sz=w800`;
  // 4. Loom — public poster
  const loom = loomIdFrom(url);
  if (loom) return `https://cdn.loom.com/sessions/thumbnails/${loom}-00001.jpg`;
  return null;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const handleCopy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Link copied to clipboard');
  } catch {
    toast.error('Could not copy link');
  }
};

const handleDownload = (url, filename) => {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const AssetThumb = ({ asset, onClick }) => {
  const meta = KIND_META[asset.kind] || KIND_META.other;
  const Icon = meta.Icon;
  const thumb = previewUrlFor(asset);
  const showPlayBadge = asset.kind === 'video' || (thumb && youtubeIdFrom(asset.url)) || loomIdFrom(asset.url);

  const stop = (e) => { e.stopPropagation(); };
  const handle = (e) => { stop(e); onClick && onClick(asset); };

  if (thumb) {
    return (
      <button
        type="button"
        onClick={handle}
        className="block w-full text-left cursor-zoom-in"
        data-testid={`asset-preview-${asset.id}`}
      >
        <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-50 overflow-hidden">
          <img
            src={thumb}
            alt={asset.display_name}
            referrerPolicy="no-referrer"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const sib = e.currentTarget.nextElementSibling;
              if (sib) sib.style.display = 'flex';
            }}
          />
          <div className={`absolute inset-0 hidden items-center justify-center bg-gradient-to-br ${meta.dot}`}>
            <Icon className="h-10 w-10 text-white/90" />
          </div>
          {showPlayBadge && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center group-hover:bg-black/75 transition-colors">
                <svg viewBox="0 0 24 24" className="h-5 w-5 ml-0.5 text-white" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
          <span className="absolute top-2 left-2 text-[9px] font-bold tracking-[0.16em] text-white uppercase px-1.5 py-0.5 rounded bg-black/55 backdrop-blur-sm">
            {meta.label}
          </span>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handle}
      className="block w-full text-left cursor-zoom-in"
      data-testid={`asset-preview-${asset.id}`}
    >
      <div className={`relative aspect-[4/3] bg-gradient-to-br ${meta.dot} flex items-center justify-center overflow-hidden`}>
        <Icon className="h-12 w-12 text-white/95" strokeWidth={1.5} />
        <span className="absolute top-3 left-3 text-[10px] font-bold tracking-[0.18em] text-white/90 uppercase">
          {meta.label}
        </span>
      </div>
    </button>
  );
};

const AssetCard = ({ asset, onRename, onPreview }) => {
  const meta = KIND_META[asset.kind] || KIND_META.other;
  const Icon = meta.Icon;

  return (
    <Card
      className="group overflow-hidden border-slate-200 hover:border-[#2EB4AC]/40 hover:shadow-lg transition-all duration-200 flex flex-col"
      data-testid={`asset-card-${asset.id}`}
    >
      <AssetThumb asset={asset} onClick={onPreview} />
      <CardContent className="p-3.5 flex-1 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <Badge variant="secondary" className={`text-[10px] border ${meta.color}`}>
            <Icon className="h-3 w-3 mr-1" />
            {meta.label}
          </Badge>
          {asset.total_versions > 1 && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              v{asset.version}/{asset.total_versions}
            </span>
          )}
        </div>

        <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2" title={asset.display_name}>
          {asset.display_name}
        </p>

        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="secondary" className="bg-[#2EB4AC]/10 text-[#2EB4AC] text-[10px]">
            {asset.brand_name}
          </Badge>
          <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-[10px]">
            {asset.platform}
          </Badge>
          {asset.rework_requested && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px]">
              <RotateCcw className="h-2.5 w-2.5 mr-1" />
              Rework
            </Badge>
          )}
        </div>

        <div className="text-[11px] text-slate-500 flex items-center justify-between mt-auto pt-2 border-t border-slate-100">
          <Link
            to={`/tasks?taskId=${encodeURIComponent(asset.task_display_id || '')}`}
            className="font-mono font-semibold text-[#2EB4AC] hover:underline"
          >
            {asset.task_display_id || '—'}
          </Link>
          <span>{formatDate(asset.completed_at || asset.created_at)}</span>
        </div>

        <div className="flex items-center gap-1 mt-1 -mb-1">
          <TooltipProvider>
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="flex-1 h-8 text-xs text-slate-600 hover:text-[#2EB4AC] hover:bg-[#2EB4AC]/10"
                  onClick={() => window.open(asset.url, '_blank', 'noopener,noreferrer')}
                  data-testid={`asset-open-${asset.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Open
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open in new tab</TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-600 hover:text-violet-600 hover:bg-violet-50"
                  onClick={() => handleCopy(asset.url)}
                  data-testid={`asset-copy-${asset.id}`}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy link</TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-600 hover:text-amber-600 hover:bg-amber-50"
                  onClick={() => onRename && onRename(asset)}
                  data-testid={`asset-rename-${asset.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Rename</TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50"
                  onClick={() => handleDownload(asset.url, asset.filename)}
                  data-testid={`asset-download-${asset.id}`}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Download as<br />{asset.filename}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
};

const AssetRow = ({ asset, onRename, onPreview }) => {
  const meta = KIND_META[asset.kind] || KIND_META.other;
  const Icon = meta.Icon;
  const thumb = previewUrlFor(asset);
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
      data-testid={`asset-row-${asset.id}`}
    >
      {thumb ? (
        <button
          type="button"
          onClick={() => onPreview && onPreview(asset)}
          className="relative w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 cursor-zoom-in"
          data-testid={`asset-preview-${asset.id}`}
        >
          <img
            src={thumb}
            alt=""
            referrerPolicy="no-referrer"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const sib = e.currentTarget.nextElementSibling;
              if (sib) sib.style.display = 'flex';
            }}
          />
          <div className={`absolute inset-0 hidden items-center justify-center bg-gradient-to-br ${meta.dot}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onPreview && onPreview(asset)}
          className={`w-12 h-12 rounded-lg bg-gradient-to-br ${meta.dot} text-white flex items-center justify-center flex-shrink-0 cursor-zoom-in`}
          data-testid={`asset-preview-${asset.id}`}
        >
          <Icon className="h-5 w-5" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate" title={asset.display_name}>
          {asset.display_name}
        </p>
        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
          <Link
            to={`/tasks?taskId=${encodeURIComponent(asset.task_display_id || '')}`}
            className="font-mono font-semibold text-[#2EB4AC] hover:underline"
          >
            {asset.task_display_id || '—'}
          </Link>
          <span>·</span>
          <span>{asset.brand_name}</span>
          <span>·</span>
          <span>{asset.platform}</span>
          {asset.assignee_name && (
            <>
              <span>·</span>
              <span className="truncate max-w-[180px]">{asset.assignee_name}</span>
            </>
          )}
        </div>
      </div>
      <div className="hidden md:flex items-center gap-2 text-[11px] text-slate-500 mr-2">
        <Badge variant="secondary" className={`text-[10px] border ${meta.color}`}>
          {meta.label}
        </Badge>
        {asset.total_versions > 1 && (
          <span className="font-semibold">v{asset.version}/{asset.total_versions}</span>
        )}
        <span>{formatDate(asset.completed_at || asset.created_at)}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(asset.url, '_blank', 'noopener,noreferrer')}>
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleCopy(asset.url)}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-slate-500 hover:text-amber-600"
          onClick={() => onRename && onRename(asset)}
          data-testid={`asset-rename-${asset.id}`}
          title="Rename"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(asset.url, asset.filename)}>
          <Download className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default function AssetLibraryPage() {
  const [searchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState({ total_assets: 0, brands_covered: 0, added_last_7d: 0, added_last_30d: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [brandFilter, setBrandFilter] = useState(searchParams.get('brand') || 'all');
  const [platformFilter, setPlatformFilter] = useState(searchParams.get('platform') || 'all');
  const [kindFilter, setKindFilter] = useState(searchParams.get('kind') || 'all');
  const [sort, setSort] = useState('newest');
  const [view, setView] = useState('grid'); // grid | list

  // Cached lookups for filter dropdowns
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');

  // Rename modal state
  const [renameAsset, setRenameAsset] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameResolving, setRenameResolving] = useState(false);
  const [resolvedName, setResolvedName] = useState(null);

  // Lightbox state
  const [previewAsset, setPreviewAsset] = useState(null);
  const openPreview = (asset) => setPreviewAsset(asset);
  const closePreview = () => setPreviewAsset(null);

  const openRename = (asset) => {
    setRenameAsset(asset);
    setRenameValue(asset.custom_name || asset.display_name || '');
    setResolvedName(null);
  };

  const closeRename = () => {
    setRenameAsset(null);
    setRenameValue('');
    setResolvedName(null);
  };

  const fetchOriginalName = async (asset) => {
    const target = asset || renameAsset;
    if (!target) return;
    setRenameResolving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/assets/resolve-name?url=${encodeURIComponent(target.url)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Could not fetch original name');
      const data = await res.json();
      if (data.name) {
        setResolvedName(data.name);
        setRenameValue(data.name);
        toast.success('Pulled name from source');
      } else {
        toast.error('Could not find an original name for this URL');
      }
    } catch (e) {
      toast.error(e.message || 'Could not resolve name');
    } finally {
      setRenameResolving(false);
    }
  };

  const useAutoName = () => {
    if (!renameAsset) return;
    setRenameValue(renameAsset.auto_name || '');
    setResolvedName(null);
  };

  const saveRename = async () => {
    if (!renameAsset) return;
    const name = (renameValue || '').trim();
    setRenameSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/assets/${encodeURIComponent(renameAsset.id)}/rename`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_name: name }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to rename');
      }
      // Optimistic update — patch in the new name into local list
      setAssets(prev => prev.map(a => a.id === renameAsset.id
        ? {
            ...a,
            display_name: name || a.auto_name,
            custom_name: name || null,
          }
        : a
      ));
      toast.success(name ? 'Renamed' : 'Custom name cleared');
      closeRename();
    } catch (e) {
      toast.error(e.message || 'Failed to rename');
    } finally {
      setRenameSaving(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [assetsRes, statsRes, typesRes] = await Promise.all([
        fetch(`${API_URL}/api/assets?limit=1000`, { credentials: 'include' }),
        fetch(`${API_URL}/api/assets/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/task-types`, { credentials: 'include' }),
      ]);
      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (typesRes.ok) setTaskTypes(await typesRes.json());
    } catch (e) {
      toast.error('Could not load Asset Library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchAll, 0);
    return () => clearTimeout(t);
  }, []);

  // Derive brand & platform lists from the loaded data (so the dropdowns only show what's actually present)
  const brands = useMemo(() => {
    const set = new Set();
    for (const a of assets) {
      if (a.brand_name) set.add(a.brand_name);
    }
    return [...set].sort();
  }, [assets]);

  const platforms = useMemo(() => {
    const set = new Set();
    for (const a of assets) {
      if (a.platform) set.add(a.platform);
    }
    return [...set].sort();
  }, [assets]);

  // Apply client-side filters & sorting (server already returns everything)
  const filtered = useMemo(() => {
    let out = assets;
    const s = search.trim().toLowerCase();
    if (s) {
      out = out.filter(a =>
        (a.display_name || '').toLowerCase().includes(s) ||
        (a.task_display_id || '').toLowerCase().includes(s) ||
        (a.brand_name || '').toLowerCase().includes(s) ||
        (a.design_brief || '').toLowerCase().includes(s) ||
        (a.task_type || '').toLowerCase().includes(s)
      );
    }
    if (brandFilter !== 'all') out = out.filter(a => a.brand_name === brandFilter);
    if (platformFilter !== 'all') out = out.filter(a => a.platform === platformFilter);
    if (kindFilter !== 'all') out = out.filter(a => a.kind === kindFilter);
    if (taskTypeFilter !== 'all') out = out.filter(a => a.task_type === taskTypeFilter);
    if (sort === 'oldest') {
      out = [...out].sort((a, b) => (a.completed_at || a.created_at || '').localeCompare(b.completed_at || b.created_at || ''));
    } else if (sort === 'brand') {
      out = [...out].sort((a, b) => (a.brand_name || '').localeCompare(b.brand_name || ''));
    } else {
      out = [...out].sort((a, b) => (b.completed_at || b.created_at || '').localeCompare(a.completed_at || a.created_at || ''));
    }
    return out;
  }, [assets, search, brandFilter, platformFilter, kindFilter, taskTypeFilter, sort]);

  // Brand shelves (sidebar on the left)
  const brandShelves = useMemo(() => {
    const counts = new Map();
    for (const a of assets) {
      const k = a.brand_name || 'Other';
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [assets]);

  const hasActiveFilters = search || brandFilter !== 'all' || platformFilter !== 'all' || kindFilter !== 'all' || taskTypeFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setBrandFilter('all');
    setPlatformFilter('all');
    setKindFilter('all');
    setTaskTypeFilter('all');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="asset-library-page">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 md:p-8">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 30% 50%, rgba(46, 180, 172, 0.5) 0%, transparent 50%),
                            radial-gradient(circle at 80% 30%, rgba(167, 139, 250, 0.4) 0%, transparent 50%)`
        }} />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen className="h-5 w-5 text-[#2EB4AC]" />
              <span className="text-[#2EB4AC] text-sm font-medium tracking-wider uppercase">Creative Asset Library</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              From brief to banner, all in one place.
            </h1>
            <p className="text-slate-400 mt-2 text-sm md:text-base">
              Auto-curated from output links submitted across all tasks. Searchable, downloadable, shareable.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 md:gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-white tabular-nums" data-testid="stat-total-assets">{stats.total_assets}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Assets</p>
            </div>
            <div className="text-center border-l border-slate-700 pl-4 md:pl-6">
              <p className="text-3xl font-bold text-white tabular-nums">{stats.brands_covered}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Brands</p>
            </div>
            <div className="text-center border-l border-slate-700 pl-4 md:pl-6">
              <p className="text-3xl font-bold text-[#2EB4AC] tabular-nums">+{stats.added_last_7d}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">Last 7d</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Brand shelves */}
        <Card className="border-slate-200 self-start sticky top-20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2 px-2">
              <Tag className="h-4 w-4 text-slate-500" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Brand Shelves</p>
            </div>
            <div className="max-h-[480px] overflow-y-auto space-y-0.5 pr-1">
              <button
                type="button"
                onClick={() => setBrandFilter('all')}
                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-md text-sm transition-colors ${
                  brandFilter === 'all'
                    ? 'bg-[#2EB4AC]/10 text-[#2EB4AC] font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="brand-shelf-all"
              >
                <span>All Brands</span>
                <span className="text-[11px] font-mono">{stats.total_assets}</span>
              </button>
              {brandShelves.map(({ name, count }) => (
                <button
                  type="button"
                  key={name}
                  onClick={() => setBrandFilter(name)}
                  className={`w-full flex items-center justify-between px-2.5 py-2 rounded-md text-sm transition-colors ${
                    brandFilter === name
                      ? 'bg-[#2EB4AC]/10 text-[#2EB4AC] font-semibold'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`brand-shelf-${name}`}
                >
                  <span className="truncate max-w-[150px]" title={name}>{name}</span>
                  <span className="text-[11px] font-mono">{count}</span>
                </button>
              ))}
              {brandShelves.length === 0 && !loading && (
                <p className="text-xs text-slate-400 px-2 py-4 text-center">No brands yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main column */}
        <div className="space-y-6 min-w-0">
          {/* Toolbar */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by Task ID, brand, brief or task type…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-10"
                    data-testid="asset-search-input"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={kindFilter} onValueChange={setKindFilter}>
                    <SelectTrigger className="h-10 w-[140px]" data-testid="asset-kind-filter">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="image">Images</SelectItem>
                      <SelectItem value="video">Videos</SelectItem>
                      <SelectItem value="figma">Figma</SelectItem>
                      <SelectItem value="drive">Drive / Dropbox</SelectItem>
                      <SelectItem value="doc">Documents</SelectItem>
                      <SelectItem value="link">Other Links</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={platformFilter} onValueChange={setPlatformFilter}>
                    <SelectTrigger className="h-10 w-[140px]" data-testid="asset-platform-filter">
                      <SelectValue placeholder="Platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      {platforms.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
                    <SelectTrigger className="h-10 w-[160px]" data-testid="asset-tasktype-filter">
                      <SelectValue placeholder="Task Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Task Types</SelectItem>
                      {taskTypes.map(t => <SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={sort} onValueChange={setSort}>
                    <SelectTrigger className="h-10 w-[130px]" data-testid="asset-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest first</SelectItem>
                      <SelectItem value="oldest">Oldest first</SelectItem>
                      <SelectItem value="brand">By Brand</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center border border-slate-200 rounded-md overflow-hidden">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-9 px-2.5 rounded-none ${view === 'grid' ? 'bg-[#2EB4AC]/10 text-[#2EB4AC]' : 'text-slate-500'}`}
                      onClick={() => setView('grid')}
                      data-testid="asset-view-grid"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-9 px-2.5 rounded-none ${view === 'list' ? 'bg-[#2EB4AC]/10 text-[#2EB4AC]' : 'text-slate-500'}`}
                      onClick={() => setView('list')}
                      data-testid="asset-view-list"
                    >
                      <ListIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-slate-500 hover:text-slate-700"
                      data-testid="asset-clear-filters"
                    >
                      <X className="h-3.5 w-3.5 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 text-[#2EB4AC]" />
                Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of {assets.length} delivered assets
                {brandFilter !== 'all' && (
                  <>
                    {' '}for <span className="font-semibold text-[#2EB4AC]">{brandFilter}</span>
                  </>
                )}
              </p>
            </CardContent>
          </Card>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EB4AC]"></div>
            </div>
          ) : filtered.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <FolderOpen className="h-7 w-7 text-slate-400" />
                </div>
                <p className="text-slate-700 font-semibold">No assets match these filters</p>
                <p className="text-slate-400 text-sm mt-1">
                  Try clearing the filters, or submit output links on completed tasks to populate the library.
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
                    <X className="h-3.5 w-3.5 mr-1" /> Clear filters
                  </Button>
                )}
                <Link to="/tasks" className="inline-block mt-4 ml-2">
                  <Button variant="outline" size="sm">
                    <ArrowUpRight className="h-3.5 w-3.5 mr-1" /> Go to Task Log
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : view === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-2">
              {filtered.map(asset => <AssetCard key={asset.id} asset={asset} onRename={openRename} onPreview={openPreview} />)}
            </div>
          ) : (
            <Card className="border-slate-200 overflow-hidden mt-2">
              <div className="divide-y divide-slate-100">
                {filtered.map(asset => <AssetRow key={asset.id} asset={asset} onRename={openRename} onPreview={openPreview} />)}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Rename Modal */}
      <Dialog open={!!renameAsset} onOpenChange={(o) => { if (!o) closeRename(); }}>
        <DialogContent
          className="max-w-lg w-[calc(100vw-2rem)] overflow-hidden"
          data-testid="asset-rename-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-amber-600" />
              Rename Asset
            </DialogTitle>
          </DialogHeader>

          {renameAsset && (
            <div className="space-y-4 w-full min-w-0">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 flex items-center gap-3 w-full min-w-0 overflow-hidden">
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200">
                  {previewUrlFor(renameAsset) ? (
                    <img
                      src={previewUrlFor(renameAsset)}
                      alt=""
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${(KIND_META[renameAsset.kind] || KIND_META.other).dot} flex items-center justify-center text-white`}>
                      <FolderOpen className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 truncate">
                    {renameAsset.brand_name} · {renameAsset.platform}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 truncate" title={renameAsset.url}>
                    {renameAsset.task_display_id} · v{renameAsset.version}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">{renameAsset.url}</p>
                </div>
              </div>

              <div>
                <Label htmlFor="rename-input" className="text-sm font-semibold text-slate-800">
                  Display name
                </Label>
                <Input
                  id="rename-input"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Type a custom name…"
                  className="mt-1.5"
                  maxLength={200}
                  data-testid="rename-input"
                />
                <div className="flex items-center justify-between mt-1.5 gap-2 flex-wrap">
                  <p className="text-[11px] text-slate-400 truncate flex-1 min-w-0">
                    {resolvedName ? (
                      <span className="inline-flex items-center gap-1 text-violet-600">
                        <Wand2 className="h-3 w-3" />
                        Pulled from source: <span className="font-mono truncate max-w-[260px]">{resolvedName}</span>
                      </span>
                    ) : (
                      <>Original auto-name: <span className="font-mono">{renameAsset.auto_name}</span></>
                    )}
                  </p>
                  <p className="text-[11px] text-slate-400 tabular-nums flex-shrink-0">{renameValue.length}/200</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchOriginalName(renameAsset)}
                  disabled={renameResolving}
                  className="text-[12px] border-violet-200 text-violet-700 hover:bg-violet-50"
                  data-testid="rename-fetch-original"
                >
                  {renameResolving ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      Fetching…
                    </>
                  ) : (
                    <>
                      <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                      Use source name
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={useAutoName}
                  className="text-[12px]"
                  data-testid="rename-use-auto"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Use auto-name
                </Button>
                {renameAsset.custom_name && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setRenameValue(''); }}
                    className="text-[12px] text-rose-600 hover:bg-rose-50"
                    data-testid="rename-clear"
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Clear custom name
                  </Button>
                )}
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-500">
                <span className="font-bold uppercase tracking-wider">Tip · </span>
                The Download button always uses the canonical filename derived from brand, platform, task type and version — your custom name only affects how the asset appears in the library.
              </div>
            </div>
          )}

          <DialogFooter className="flex-row flex-wrap gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={closeRename}
              disabled={renameSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveRename}
              disabled={renameSaving}
              className="bg-[#2EB4AC] hover:bg-[#269d96] text-white"
              data-testid="rename-save-btn"
            >
              {renameSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save name
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox preview */}
      <Dialog open={!!previewAsset} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent
          className="max-w-5xl p-0 bg-slate-950 border-slate-800 overflow-hidden"
          data-testid="asset-lightbox"
        >
          {previewAsset && (() => {
            const url = previewAsset.url;
            const yt = youtubeIdFrom(url);
            const drv = driveIdFrom(url);
            const loom = loomIdFrom(url);
            const isImage = previewAsset.kind === 'image';

            // Decide what to render in the lightbox body
            let body = null;
            if (yt) {
              body = (
                <div className="relative w-full aspect-video bg-black">
                  <iframe
                    src={`https://www.youtube.com/embed/${yt}?autoplay=1&rel=0`}
                    title={previewAsset.display_name}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
              );
            } else if (loom) {
              body = (
                <div className="relative w-full aspect-video bg-black">
                  <iframe
                    src={`https://www.loom.com/embed/${loom}?autoplay=1`}
                    title={previewAsset.display_name}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
              );
            } else if (drv) {
              body = (
                <div className="relative w-full aspect-[16/10] bg-black">
                  <iframe
                    src={`https://drive.google.com/file/d/${drv}/preview`}
                    title={previewAsset.display_name}
                    allow="autoplay"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
              );
            } else if (isImage) {
              body = (
                <div className="bg-slate-950 flex items-center justify-center" style={{ minHeight: 320, maxHeight: '80vh' }}>
                  <img
                    src={url}
                    alt={previewAsset.display_name}
                    referrerPolicy="no-referrer"
                    className="max-h-[80vh] w-auto object-contain"
                  />
                </div>
              );
            } else {
              // Generic links — show a centered "open externally" prompt
              body = (
                <div className="bg-slate-950 text-slate-200 flex flex-col items-center justify-center gap-5 py-24 px-8">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${(KIND_META[previewAsset.kind] || KIND_META.other).dot} flex items-center justify-center`}>
                    {(() => {
                      const Ico = (KIND_META[previewAsset.kind] || KIND_META.other).Icon;
                      return <Ico className="h-9 w-9 text-white" />;
                    })()}
                  </div>
                  <div className="text-center max-w-xl">
                    <p className="text-lg font-semibold">In-line preview is not supported for this provider</p>
                    <p className="text-sm text-slate-400 mt-1 break-all">{url}</p>
                  </div>
                  <Button
                    className="bg-[#2EB4AC] hover:bg-[#269d96] text-white"
                    onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in new tab
                  </Button>
                </div>
              );
            }

            return (
              <>
                <DialogHeader className="px-5 py-3 border-b border-slate-800 flex-row items-center justify-between gap-3">
                  <div className="min-w-0 flex-1 text-left">
                    <DialogTitle className="text-white text-sm md:text-base font-semibold truncate" title={previewAsset.display_name}>
                      {previewAsset.display_name}
                    </DialogTitle>
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                      {previewAsset.brand_name} · {previewAsset.platform} · {previewAsset.task_display_id}
                      {previewAsset.total_versions > 1 ? ` · v${previewAsset.version}/${previewAsset.total_versions}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-300 hover:text-white hover:bg-white/10 h-8"
                      onClick={() => handleCopy(url)}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" />
                      Copy
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-300 hover:text-white hover:bg-white/10 h-8"
                      onClick={() => handleDownload(url, previewAsset.filename)}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" />
                      Download
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-300 hover:text-white hover:bg-white/10 h-8"
                      onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Open
                    </Button>
                  </div>
                </DialogHeader>
                {body}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
